(ns hai.admin-ui.profile
  "User profile page: account info and personal settings."
  (:require
   [clojure.string :as str]
   [hai.datetime :as datetime]
   [hai.admin-ui.profile.browser-extension :as ext]
   [hai.admin-ui.profile.setup :as profile-setup]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.auth :as auth]
   [hai.web-app.feature-flag.ui :as ff-ui]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ── Sections ──────────────────────────────────────────────────────────────────

;; The app chrome behind this page is Chalk, so a section lifts off it by being
;; the lighter surface. The separation is deliberately quiet: a hairline in Rock
;; carries the edge so the fills don't have to shout.
(o/defstyled section :section
  :flex :flex-col
  {:background-color t/--colour-linen
   :border "1px solid var(--colour-rock)"
   :border-radius "16px"
   :gap "16px"
   :padding "24px 28px"}
  [:h2 {:font-size "20px" :font-weight "700"
        :color t/--colour-ink
        :margin "0"}])

(defn- section-view
  [title & children]
  (into [section [:h2 title]] children))

;; Two sections abreast. They stretch to a shared height rather than each
;; hugging its own content, so the row reads as one band.
(o/defstyled section-row :div
  {:display "grid"
   :grid-template-columns "repeat(2, minmax(0, 1fr))"
   :gap "24px"}
  [:at-media {:max-width "900px"}
   {:grid-template-columns "1fr"}])

(o/defstyled section-lead :p
  {:font-size "14px"
   :color t/--colour-dim
   :margin "0"
   :max-width "60ch"})

;; Outlined rather than filled: every section's call to action is a way out of
;; this page, not the thing the page is for.
(o/defstyled section-button :a
  :inline-flex :items-center
  {:width "fit-content"
   :gap "6px"
   :padding "10px 16px"
   :border "1px solid var(--colour-rock)"
   :border-radius "10px"
   :font-size "14px" :font-weight "700"
   :color t/--text-secondary
   :text-decoration "none"
   :transition "background-color 0.15s ease, border-color 0.15s ease"}
  [:&:hover {:background-color t/--colour-chalk
             :border-color t/--colour-dim}])

;; ── Profile card ──────────────────────────────────────────────────────────────

(o/defstyled profile-identity-row :div
  :flex :items-center {:gap "12px"})

(o/defstyled profile-avatar :div
  :flex :items-center :justify-center :flex-shrink-0
  {:width "44px"
   :height "44px"
   :border-radius "50%"
   :background-color t/--colour-chalk
   :color t/--text-secondary-50
   :font-size "15px"
   :font-weight "700"
   :letter-spacing "0.03em"
   :user-select "none"})

(o/defstyled profile-name :div
  {:font-size "16px" :font-weight "600" :color t/--text-secondary})

(o/defstyled profile-field-row :div
  :flex :items-center {:gap "8px" :min-height "24px"}
  [:svg {:width "14px" :height "14px" :flex-shrink "0" :opacity "0.5"}])

(o/defstyled profile-field-text :span
  {:font-size "13px" :color t/--text-secondary :opacity "0.75"})

(defn- full-name-field []
  (r/with-let [!identity    (<s :auth/identity)
               !editing?    (r/atom false)
               !draft       (r/atom "")]
    (let [{:user/keys [uuid full-name]} @!identity
          save!       (fn []
                        (>e [:resolve
                             `[(~'user/update
                                ~{:user/uuid uuid
                                  :user/full-name @!draft})]
                             {:on-success-fx [[:dispatch [:auth/init]]]}])
                        (reset! !editing? false))]
      (if @!editing?
        [c/Flex {:gap "2" :align "center"}
         [c/TextFieldRoot
          ;; Uncontrolled (`:defaultValue`, not `:value`): `c/TextFieldRoot` is a Radix
          ;; component, so Reagent can't apply its controlled-input caret-preservation
          ;; workaround and a controlled `:value` snaps the cursor to the end on every
          ;; keystroke. `!draft` is seeded before this branch mounts and kept in sync by
          ;; `:on-change` for `save!`, so the DOM owning the value is fine.
          {:defaultValue @!draft
           :autoFocus true
           :placeholder "Your name"
           :on-change #(reset! !draft (.. % -target -value))
           :on-key-down (fn [e]
                          (when (= (.-key e) "Enter")
                            (save!)))}]
         [c/Button {:size "1" :on-click save!} "Save"]
         [c/Button {:size "1" :variant "soft"
                    :on-click #(reset! !editing? false)}
          "Cancel"]]
        [c/Flex {:gap "2" :align "center"}
         [profile-name (or full-name "—")]
         [c/Button {:size "1" :variant "ghost"
                    :on-click (fn []
                                (reset! !draft (or full-name ""))
                                (reset! !editing? true))}
          "Edit"]]))))

(defn- github-username-field []
  (r/with-let [!identity    (<s :auth/identity)
               !editing?    (r/atom false)
               !draft       (r/atom "")]
    (let [{:user/keys [uuid]} @!identity
          gh-username (:user-config.timeline.github/my-actions-filter-username @!identity)
          save!       (fn []
                        (>e [:resolve
                             `[(~'user/update
                                ~{:user/uuid uuid
                                  :user-config.timeline.github/my-actions-filter-username @!draft})]
                             {:on-success-fx [[:dispatch [:auth/init]]]}])
                        (reset! !editing? false))]
      [profile-field-row
       [icons/github {:size 14}]
       (if @!editing?
         [c/Flex {:gap "2" :align "center"}
          [c/TextFieldRoot
           ;; Uncontrolled — see the note in `full-name-field`. A controlled `:value` on
           ;; this Radix input snaps the cursor to the end each keystroke.
           {:defaultValue @!draft
            :autoFocus true
            :placeholder "e.g. octocat"
            :on-change #(reset! !draft (.. % -target -value))
            :on-key-down (fn [e]
                           (when (= (.-key e) "Enter")
                             (save!)))}]
          [c/Button {:size "1" :on-click save!} "Save"]
          [c/Button {:size "1" :variant "soft"
                     :on-click #(reset! !editing? false)}
           "Cancel"]]
         [c/Flex {:gap "2" :align "center"}
          [profile-field-text (or gh-username "\u2014")]
          [c/Button {:size "1" :variant "ghost"
                     :on-click (fn []
                                 (reset! !draft (or gh-username ""))
                                 (reset! !editing? true))}
           "Edit"]])])))

(defn- sign-out-confirmation
  "Radix AlertDialog for the sign-out flow."
  [!open?]
  [c/AlertDialogRoot
   {:open @!open?
    :onOpenChange #(reset! !open? %)}
   [c/AlertDialogContent
    [c/AlertDialogTitle "Sign out"]
    [c/AlertDialogDescription
     "Are you sure you want to sign out?"]
    [c/Flex {:justify "end" :gap "3" :mt "4"}
     [c/AlertDialogCancel
      [c/Button {:variant "soft"} "Cancel"]]
     [c/AlertDialogAction
      [c/Button {:variant "soft"
                 :color "red"
                 :on-click #(>e [:auth/logout])}
       "Sign out"]]]]])

(defn- sign-out-button []
  (r/with-let [!open? (r/atom false)]
    [:<>
     [c/Button {:variant "soft" :color "red"
                :on-click #(reset! !open? true)}
      [icons/outlined-power_settings_new]
      "Sign out"]
     [sign-out-confirmation !open?]]))

;; ── Set up Humble ─────────────────────────────────────────────────────────────

(defn- set-up-humble-section []
  [section-view "Get the most out of Humble"
   [section-lead profile-setup/lead-text]
   [profile-setup/steps-view]])

;; ── Connections ───────────────────────────────────────────────────────────────

(defn- connections-section []
  [section-view "Integrate your existing apps & tools"
   [section-lead
    "Connect your tools and manage your connections — calendar, Airtable, Notion, HubSpot and more."]
   [section-button {:href (routing/href :connections/table)}
    "Manage connections"
    [:span "›"]]])

;; ── Chrome extension ──────────────────────────────────────────────────────────

(defn- browser-extension-section []
  [section-view ext/section-title
   [section-lead ext/lead-text]
   [ext/settings-view]])

;; ── Humble plan ────────────────────────────────────────────────────────────────

;; Unpadded: the section around it supplies the inset.
(o/defstyled plan-card :div
  :flex :flex-col
  {:gap "6px"
   :max-width "420px"})

(o/defstyled plan-name :div
  {:font-size "18px" :font-weight "600" :color t/--text-secondary})

(o/defstyled plan-detail :div
  {:font-size "13px" :color t/--text-secondary :opacity "0.75"})

(o/defstyled manage-link :a
  ;; New-tab icon fades in on hover — same treatment as the contact card links.
  :flex :items-center
  {:width "fit-content"
   :gap "4px"
   :margin-top "6px"
   :font-size "13px"
   :font-weight "600"
   :color t/--colour-purple
   :text-decoration "none"}
  [:svg {:width "13px" :height "13px" :opacity "0" :transition "opacity 0.15s"}]
  [:&:hover {:text-decoration "underline"}
   [:svg {:opacity "1"}]])

(def human-readable-plan
  {"pioneers_annual" "Humble Pioneers (Annual)"
   "pioneers_monthly" "Humble Pioneers (Monthly)"
   "founding_heroes_lifetime" "Humble Legends (Lifetime access)"})

(defn- humble-plan-section []
  (let [{:user/keys [email subscription]
         :stripe/keys [billing-portal-url]} @(<s :auth/identity)
        active? (and (some? subscription)
                     (not= "canceled" (:status subscription)))]
    [section-view "Humble plan"
     ;; Keep these as flat, direct children of plan-card. A `[:<>]` fragment as
     ;; the first child makes ornament recurse into it and drop the siblings
     ;; (e.g. the manage-link) — see expand-hiccup-tag-simple in ornament.
     [plan-card
      [plan-name (if active?
                   (or (human-readable-plan (:humble-plan subscription)) "—")
                   "Free plan")]
      (when active?
        (when-let [ends (:current-period-end subscription)]
          [plan-detail (str "Current period ends " (datetime/format-date ends))]))
      [manage-link {:href   (str billing-portal-url "?prefilled_email="
                                 (js/encodeURIComponent (first email)))
                    :target "_blank"
                    :rel    "noopener"}
       "Manage subscription"
       [icons/outlined-north_east]]]]))

(defn- profile-section []
  (let [{:user/keys [email full-name display-name]} @(<s :auth/identity)
        person-name (or full-name display-name)]
    [section-view "Profile"
     [profile-identity-row
      [profile-avatar (if (seq person-name) (layout/initials person-name) [icons/outlined-person])]
      [full-name-field]]
     [profile-field-row [icons/outlined-email] [profile-field-text (str/join ", " email)]]
     [github-username-field]]))

(defn profile-page
  [_]
  [layout/page
   [layout/heading]
   [layout/lead
    "Make Humble yours. Control how you appear, your preferences and settings."]
   [layout/content
    [section-row
     [profile-section]
     [humble-plan-section]]
    [set-up-humble-section]
    [connections-section]
    [browser-extension-section]
    [section-view ff-ui/section-title
     [ff-ui/feature-flags-panel]]

    [c/Flex {:mt "2"} [sign-out-button]]]])

(defn routes []
  [["/profile" {:name :admin-ui/profile
                :label "My Profile"
                :icon icons/outlined-person
                ::routing/window-title "Profile"
                :view profile-page
                ;; The setup section's calendar step branches on whether an
                ;; account of that provider is already connected.
                :controllers [{:start (fn [] (>e [:timeline/load-connection-status]))}]}]])
